import { datePickerMinDateMaxDateValidate } from '../../../utils/dateUtil';
import { IfspTransitionDataType } from '../../../redux/features/ifsp/ifspTransitionSlice';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../redux/hooks';

export type IfspTransitionValidationFormResultType = {
  transitionPreparationDate?: boolean;
  transitionPreparationDateErrorMessageId?: string;
  transitionPreparationDateErrorMessage?: string;

  transitionPlanDate?: boolean;
  transitionPlanDateErrorMessageId?: string;
  transitionPlanDateErrorMessage?: string;

  noTransitionPlanReason?: boolean;
  noTransitionPlanReasonErrorMessageId?: string;
  noTransitionPlanReasonErrorMessage?: string;

  meetingDate?: boolean;
  meetingDateErrorMessageId?: string;
  meetingDateErrorMessage?: string;

  noTPCReason?: boolean;
  noTPCReasonErrorMessageId?: string;
  noTPCReasonErrorMessage?: string;

  transitionConferenceDeclinedDate?: boolean;
  transitionConferenceDeclinedDateErrorMessageId?: string;
  transitionConferenceDeclinedDateErrorMessage?: string;

  consentDate?: boolean;
  consentDateErrorMessageId?: string;
  consentDateErrorMessage?: string;

  dateOfReferralToLea?: boolean;
  dateOfReferralToLeaErrorMessageId?: string;
  dateOfReferralToLeaErrorMessage?: string;

  leaOptOutDate?: boolean;
  leaOptOutDateErrorMessageId?: string;
  leaOptOutDateErrorMessage?: string;
};

export const useIfspTransitionValidationCheckToSubmit = (
  ifspTransitionDetails: IfspTransitionDataType,
): IfspTransitionValidationFormResultType => {
  const clientDob =
    useAppSelector(
      (state) => state.ifspClientDetails.ifspClientDetails.client.dateOfBirth,
    ) || '';

  // Helper function
  const checkDateOutOfRange = (dobStr: string, targetDateStr: string) => {
    if (!dobStr || !targetDateStr) return false;

    const dob = dayjs(dobStr, 'MM/DD/YYYY');
    const targetDate = dayjs(targetDateStr);

    const monthsDiff = targetDate.diff(dob, 'month');
    const thirtySixMonthsFromDob = dob.add(36, 'month');
    const ninetyDaysBeforeThirtySixMonths = thirtySixMonthsFromDob.subtract(
      90,
      'day',
    );

    return (
      monthsDiff < 27 || targetDate.isAfter(ninetyDaysBeforeThirtySixMonths)
    );
  };

  // Start with empty result
  const validationResult: IfspTransitionValidationFormResultType = {};

  //  =============== IFSP TRANSITION PREPARATION DATE ===============
  if (
    ifspTransitionDetails.transitionPreparation.transitionPreparationDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transitionPreparation.transitionPreparationDate,
    )
  ) {
    Object.assign(validationResult, {
      transitionPreparationDate: true,
      transitionPreparationDateErrorMessageId:
        'datePickerMinDateMaxDateValidate',
      transitionPreparationDateErrorMessage:
        'Transition Preparation Date is invalid',
    });
  }

  //  =============== IFSP TRANSITION PLAN DATE ===============
  if (
    ifspTransitionDetails.transition.transitionPlanDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.transitionPlanDate,
    )
  ) {
    Object.assign(validationResult, {
      transitionPlanDate: true,
      transitionPlanDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      transitionPlanDateErrorMessage: 'Transition Plan Date is invalid',
    });
  } else if (
    !ifspTransitionDetails.transition.transitionPlanDate &&
    ifspTransitionDetails.transition.isTransitionPlanDone
  ) {
    Object.assign(validationResult, {
      transitionPlanDate: true,
      transitionPlanDateErrorMessageId: 'IFSP.TransitionPlanDateRequired.Error',
      transitionPlanDateErrorMessage: 'Transition Plan Date is required',
    });
  }

  //  =============== NO TRANSITION PLAN REASON ===============

  if (
    (ifspTransitionDetails.transition.transitionPlanDate &&
      !ifspTransitionDetails.transition.noTransitionPlanReason &&
      checkDateOutOfRange(
        clientDob || '',
        ifspTransitionDetails.transition.transitionPlanDate,
      )) ||
    (ifspTransitionDetails.transition.isTransitionPlanDone === false &&
      !ifspTransitionDetails.transition.noTransitionPlanReason)
  ) {
    Object.assign(validationResult, {
      noTransitionPlanReason: true,
      noTransitionPlanReasonErrorMessageId:
        'IFSP.TransitionPlanDateOutOfRange.Error',
      noTransitionPlanReasonErrorMessage:
        'Reason for “No transition plan” or outside “expected” timeframe',
    });
  }

  //  =============== IFSP MEETING DATE ===============
  if (
    ifspTransitionDetails.transition.meetingDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.meetingDate,
    )
  ) {
    Object.assign(validationResult, {
      meetingDate: true,
      meetingDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      meetingDateErrorMessage: 'Meeting Date is invalid',
    });
  } else if (
    !ifspTransitionDetails.transition.meetingDate &&
    ifspTransitionDetails.transition.isTpcMeetingDone
  ) {
    Object.assign(validationResult, {
      meetingDate: true,
      meetingDateErrorMessageId: 'IFSP.MeetingDateRequired.Error',
      meetingDateErrorMessage: 'Meeting Date is required',
    });
  }

  //  =============== NO TPC REASON ===============

  if (
    ifspTransitionDetails.transition.meetingDate &&
    !ifspTransitionDetails.transition.noTpcReason &&
    checkDateOutOfRange(
      clientDob || '',
      ifspTransitionDetails.transition.meetingDate,
    )
  ) {
    Object.assign(validationResult, {
      noTPCReason: true,
      noTPCReasonErrorMessageId: 'IFSP.MeetingDateOutOfRange.Error',
      noTPCReasonErrorMessage:
        'Reason for "No TC" or outside "expected" time frame is required',
    });
  }

  //  =============== TPC DECLINE DATE ===============
  if (
    ifspTransitionDetails.transition.transitionConferenceDeclinedDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.transitionConferenceDeclinedDate,
    )
  ) {
    Object.assign(validationResult, {
      transitionConferenceDeclinedDate: true,
      transitionConferenceDeclinedDateErrorMessageId:
        'datePickerMinDateMaxDateValidate',
      transitionConferenceDeclinedDateErrorMessage:
        'Family Decline Date is invalid',
    });
  } else if (
    !ifspTransitionDetails.transition.transitionConferenceDeclinedDate &&
    ifspTransitionDetails.transition.noTpcReason === 'FAMILYDECLINED'
  ) {
    Object.assign(validationResult, {
      transitionConferenceDeclinedDate: true,
      transitionConferenceDeclinedDateErrorMessageId:
        'IFSP.TransitionConferenceDeclinedDateRequired.Error',
      transitionConferenceDeclinedDateErrorMessage:
        'Family Decline Date is required',
    });
  } else if (
    dayjs(
      ifspTransitionDetails.transition.transitionConferenceDeclinedDate,
      'MM/DD/YYYY',
    ).isAfter(dayjs())
  ) {
    Object.assign(validationResult, {
      transitionConferenceDeclinedDate: true,
      transitionConferenceDeclinedDateErrorMessageId:
        'IFSP.TransitionConferenceDeclinedDateCannotBeFutureDate.Error',
      transitionConferenceDeclinedDateErrorMessage:
        'Family Decline Date cannot be future date',
    });
  }

  //  =============== IFSP CONSENT DATE ===============
  if (
    ifspTransitionDetails.transition.consentDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.consentDate,
    )
  ) {
    Object.assign(validationResult, {
      consentDate: true,
      consentDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      consentDateErrorMessage: 'Consent Date is invalid',
    });
  } else if (
    !ifspTransitionDetails.transition.consentDate &&
    ifspTransitionDetails.transition.leaConsent
  ) {
    Object.assign(validationResult, {
      consentDate: true,
      consentDateErrorMessageId: 'IFSP.ConsentDateRequired.Error',
      consentDateErrorMessage: 'Consent Date is required',
    });
  }

  //  =============== DATE OF REFERRAL TO LEA ===============
  if (
    ifspTransitionDetails.transition.dateOfReferralToLea &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.dateOfReferralToLea,
    )
  ) {
    Object.assign(validationResult, {
      dateOfReferralToLea: true,
      dateOfReferralToLeaErrorMessageId: 'datePickerMinDateMaxDateValidate',
      dateOfReferralToLeaErrorMessage: 'Date of Referral to LEA is invalid',
    });
  }

  //  =============== LEA OPT OUT DATE ===============
  if (
    ifspTransitionDetails.transition.leaOptOutDate &&
    datePickerMinDateMaxDateValidate(
      ifspTransitionDetails.transition.leaOptOutDate,
    )
  ) {
    Object.assign(validationResult, {
      leaOptOutDate: true,
      leaOptOutDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      leaOptOutDateErrorMessage: 'LEA Opt Out Date is invalid',
    });
  } else if (
    !ifspTransitionDetails.transition.leaOptOutDate &&
    ifspTransitionDetails.transition.seaStatus === 'OPTOUT'
  ) {
    Object.assign(validationResult, {
      leaOptOutDate: true,
      leaOptOutDateErrorMessageId: 'IFSP.LeaOptOutDateRequired.Error',
      leaOptOutDateErrorMessage: 'LEA Opt-out notification date is required',
    });
  }

  return validationResult;
};
